import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

import { CloseIcon } from '../../../../assets/icons/CloseIcon';
import Button from '../../../../components/button/Button';
import DialogView from '../../../../components/dialog-view/DialogView';
import IconButton from '../../../../components/icon-button/IconButton';
import Typography from '../../../../components/typography/Typography';
import NotificationService from '../../../../services/NotificationService';
import { log } from '../../../../utils/logging/log';
import { DialogFooter, DialogHeader } from '../../../goods-view/components/Styles';
import TextRow from '../components/TextRow';

export interface WorkPermitMessageAction {
  text: string;
  errorText: string;
  onSubmit: (message: string) => Promise<void>;
}

interface Props {
  open: boolean;
  title: string;
  description: string;
  messageRequired?: boolean;
  actions: WorkPermitMessageAction[];
  handleClose: () => void;
}

const WorkPermitMessageDialog = (props: Props) => {
  const { open, title, description, messageRequired, actions, handleClose } = props;
  const [message, setMessage] = useState('');
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [submittingAction, setSubmittingAction] = useState<string>();

  useEffect(() => {
    if (!open) return;
    setMessage('');
    setHasSubmitted(false);
  }, [open]);

  const isMessageMissing = !!messageRequired && !message.trim();

  const handleSubmit = useCallback(
    async (action: WorkPermitMessageAction) => {
      setHasSubmitted(true);
      if (isMessageMissing) return;

      setSubmittingAction(action.text);
      try {
        await action.onSubmit(message.trim());
        handleClose();
      } catch (error) {
        log(error);
        NotificationService.error(action.errorText);
      } finally {
        setSubmittingAction(undefined);
      }
    },
    [handleClose, isMessageMissing, message]
  );

  if (!open) return null;

  return (
    <DialogView handleClose={handleClose}>
      <Container>
        <StyledDialogHeader>
          <Typography variant="h4" fontWeight="bold">
            {title}
          </Typography>
          <IconButton variant="outlined" onClick={handleClose}>
            <CloseIcon size="18px" />
          </IconButton>
        </StyledDialogHeader>

        <Content>
          <Typography>{description}</Typography>
          <TextRow
            inputMode
            type="multilineText"
            label="Besked"
            value={message}
            onChange={(value) => setMessage(value)}
            required={messageRequired}
            error={hasSubmitted && isMessageMissing}
            helperText={hasSubmitted && isMessageMissing ? 'Besked er påkrævet' : ''}
          />
        </Content>

        <DialogFooter>
          <Buttons>
            <Button variant="secondary" onClick={handleClose}>
              Fortryd
            </Button>
            {actions.map((action) => (
              <Button
                key={action.text}
                onClick={() => handleSubmit(action)}
                disabled={!!submittingAction}
                isLoading={submittingAction === action.text}
              >
                {action.text}
              </Button>
            ))}
          </Buttons>
        </DialogFooter>
      </Container>
    </DialogView>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px;
  width: 85vw;
  max-width: 560px;
  box-sizing: border-box;
`;

const StyledDialogHeader = styled(DialogHeader)`
  width: 100%;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 16px;
  padding: 24px 0;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  column-gap: 12px;
  width: 100%;
`;

export default WorkPermitMessageDialog;
